import { useMemo } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { HANDOFFS_QUERY_KEY } from 'constants/QueryKeys.constants';

import useRequiredParams from '@polyai/common/hooks/useRequiredParams';

import api from 'api';
import type { HTTPError } from 'api/error';
import type {
  CreateHandoffRequest,
  HandoffResponse,
  HandoffsResponse,
} from 'api/resources/handoff/types';
import { curryPostFunction } from 'api/utils';
import { useDraft } from 'hooks/useDraft';
import useDraftMutation from 'hooks/useDraftMutation';
import {
  PROJECT_RESOURCE,
  useProjectResource,
} from 'hooks/useProjectResources';
import useSampleProject from 'hooks/useSampleProjects/useSampleProject';
import { useRestApiEnabled } from 'hooks/useStreamSwitch';
import { useToast } from 'hooks/useToast';
import { pick } from 'lib/miscHelper';

interface CreateHandoffArgs extends CreateHandoffRequest {
  isDuplicate: boolean;
}

const useHandoff = (
  enabled: boolean = true,
  overrideData?: HandoffsResponse | null, // pass null to override with empty data
) => {
  const { accountId, projectId } = useRequiredParams<{
    accountId: string;
    projectId: string;
  }>();
  const { isProjectResourceEnabled } = useProjectResource();
  const isEnabled =
    enabled &&
    (isProjectResourceEnabled(PROJECT_RESOURCE.HANDOFFS) ||
      isProjectResourceEnabled(PROJECT_RESOURCE.KNOWLEDGE_BASE) ||
      isProjectResourceEnabled(PROJECT_RESOURCE.RULES));
  const { inSampleProject } = useSampleProject();
  const isRestApiEnabled = useRestApiEnabled();

  const toast = useToast();

  const queryKey = [accountId, projectId, HANDOFFS_QUERY_KEY];

  const queryClient = useQueryClient();

  const { onProjectUpdate } = useDraft();

  const {
    data: handoffResponse,
    isLoading: isLoadingHandoffs,
    error: handoffsError,
  } = useQuery(
    queryKey,
    overrideData === undefined
      ? () => api.getHandoffs(accountId, projectId)
      : () => overrideData || undefined,
    {
      enabled: isEnabled && !inSampleProject && isRestApiEnabled,
      retry: false,
    },
  );

  const { mutate: createHandoffConfig, isLoading: isCreatingHandoffConfig } =
    useDraftMutation<HandoffResponse, HTTPError, CreateHandoffArgs>(
      request =>
        api.createHandoffConfig({
          accountId,
          projectId,
          request: pick(
            request,
            'name',
            'description',
            'sip_type',
            'sip_config',
            'sip_headers',
          ),
        }),
      {
        retry: false,
        mutationKey: queryKey,
        onSuccess: (data, args) => {
          queryClient.setQueryData(queryKey, {
            handoffs: [data, ...(handoffResponse?.handoffs ?? [])],
            active_handoffs: [
              data,
              ...(handoffResponse?.active_handoffs ?? []),
            ],
          });
          if (args.isDuplicate) {
            toast.success({
              title: 'Handoff duplicated',
            });
          } else {
            toast.success({
              title: 'Handoff added',
            });
          }
          onProjectUpdate();
        },
        onError: (_, args) => {
          if (args.isDuplicate) {
            toast.error({
              title: 'Failed to duplicate handoff',
            });
          } else {
            toast.error({
              title: 'Failed to add handoff',
            });
          }
        },
      },
    );

  const { mutate: editHandoffConfig, isLoading: isEditingHandoffConfig } =
    useDraftMutation(
      curryPostFunction(api.editHandoffConfig)({ accountId, projectId }),
      {
        retry: false,
        mutationKey: queryKey,
        onSuccess: data => {
          queryClient.setQueryData(queryKey, {
            handoffs: handoffResponse?.handoffs.map(h =>
              h.id === data.id ? data : h,
            ),
            active_handoffs: handoffResponse?.active_handoffs.map(h =>
              h.id === data.id ? data : h,
            ),
          });
          onProjectUpdate();
          toast.success({
            title: 'Handoff updated',
          });
        },
        onError: () => {
          toast.error({
            title: 'Failed to update handoff',
          });
        },
      },
    );

  const { mutate: deleteHandoffConfig, isLoading: isDeletingHandoffConfig } =
    useDraftMutation(
      curryPostFunction(api.deleteHandoffConfig)({ accountId, projectId }),
      {
        retry: false,
        mutationKey: queryKey,
        onSuccess: () => {
          queryClient
            .invalidateQueries(queryKey)
            .catch(e =>
              console.error('Error occurred. Could not invalidate queries', e),
            );
          onProjectUpdate();
          toast.success({
            title: 'Handoff deleted',
          });
        },
        onError: () => {
          toast.error({
            title: 'Failed to delete handoff',
          });
        },
      },
    );

  const { mutate: updateDefaultHandoff, isLoading: isUpdatingDefaultHandoff } =
    useDraftMutation(
      curryPostFunction(api.updateDefaultHandoff)({
        accountId,
        projectId,
      }),
      {
        retry: false,
        mutationKey: queryKey,
        onSuccess: data => {
          queryClient.setQueryData(queryKey, {
            handoffs: data.handoffs,
            active_handoffs: data.handoffs.filter(h => h.active),
          });
          onProjectUpdate();
          toast.success({
            title: 'Default handoff updated',
          });
        },
        onError: () => {
          toast.error({
            title: 'Failed to update default handoff',
          });
        },
      },
    );

  const handoffs = useMemo(
    () => handoffResponse?.handoffs ?? [],
    [handoffResponse?.handoffs],
  );

  const activeHandoffs = useMemo(
    () => handoffResponse?.active_handoffs ?? [],
    [handoffResponse?.active_handoffs],
  );

  const defaultHandoff = useMemo(
    () => handoffs.find(h => h.is_default),
    [handoffs],
  );

  return useMemo(
    () => ({
      handoffs,
      handoffsError,
      activeHandoffs,
      defaultHandoff,
      isLoadingHandoffs: isEnabled && isLoadingHandoffs,
      createHandoffConfig,
      isCreatingHandoffConfig,
      editHandoffConfig,
      isEditingHandoffConfig,
      deleteHandoffConfig,
      isDeletingHandoffConfig,
      updateDefaultHandoff,
      isUpdatingDefaultHandoff,
      // TODO: This needs fixing
      isKbUsingAnyDeletedHandoffs: false,
    }),
    [
      isEnabled,
      activeHandoffs,
      createHandoffConfig,
      defaultHandoff,
      deleteHandoffConfig,
      editHandoffConfig,
      handoffs,
      handoffsError,
      isCreatingHandoffConfig,
      isDeletingHandoffConfig,
      isEditingHandoffConfig,
      isLoadingHandoffs,
      isUpdatingDefaultHandoff,
      updateDefaultHandoff,
    ],
  );
};

export default useHandoff;
